import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { filterMovements } from '@/services/inventoryMovements/movementRepository';
import {
  listMovements,
  listInventoryLocations,
  MOVEMENT_TYPE_LABELS,
} from '@/services/inventoryMovements';
import type {
  InventoryMovementType,
  MovementFilters,
} from '@/services/inventoryMovements/types';
import type { InventoryMovement } from '@/services/inventoryMovements/types';
import { modulePaths } from '@/routes/paths';
import { listItems } from '@/modules/inventory/services';
import { useAuth } from '@/shared/hooks/useAuth';

const TYPE_OPTIONS: Array<InventoryMovementType | 'all'> = [
  'all',
  'check_out',
  'check_in',
  'transfer',
  'maintenance_out',
  'maintenance_in',
  'repair_out',
  'repair_in',
  'manual_adjustment',
  'inventory_count',
];

export function MovementsListPage() {
  const { hasPermission } = useAuth();
  const canRegister =
    hasPermission('inventory.movements.write') ||
    hasPermission('inventory.movements.logistics');

  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<InventoryMovementType | 'all'>(
    'all',
  );
  const [itemId, setItemId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<{ id: string; name: string }[]>([]);
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    void Promise.all([listMovements(), listItems(), listInventoryLocations()])
      .then(([movs, its, locs]) => {
        setMovements(movs);
        setItems(its.map((i) => ({ id: i.id, name: i.name })));
        setLocations(locs.map((l) => ({ id: l.id, name: l.name })));
      })
      .finally(() => setLoading(false));
  }, []);

  const display = useMemo(() => {
    const f: MovementFilters = {
      type: typeFilter,
      ...(itemId ? { inventoryItemId: itemId } : {}),
      ...(locationId ? { locationId } : {}),
      ...(dateFrom ? { fromDate: dateFrom } : {}),
      ...(dateTo ? { toDate: dateTo } : {}),
      search,
    };
    return filterMovements(movements, f);
  }, [movements, typeFilter, itemId, locationId, dateFrom, dateTo, search]);

  return (
    <section>
      <div className="inventory-page-header">
        <h1>Movimientos de inventario</h1>
        <Link to={modulePaths.inventory} className="auth-button auth-button--secondary">
          ← Artículos
        </Link>
      </div>

      <p className="auth-muted">
        Historial de movimientos (solo lectura en esta pantalla).
        {canRegister
          ? ' Para registrar check-out, transferencias o conteos, abrí un artículo en Inventario y usá la sección Movimientos en su ficha.'
          : ' Tu rol no puede registrar movimientos desde la ficha del artículo.'}
      </p>

      <div className="reservation-filters users-form-grid">
        <label className="auth-label">
          Tipo
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as InventoryMovementType | 'all')
            }
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t === 'all' ? 'Todos' : MOVEMENT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          Artículo
          <select value={itemId} onChange={(e) => setItemId(e.target.value)}>
            <option value="">Todos</option>
            {items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          Ubicación
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">Todas</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          Desde
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="auth-label">
          Hasta
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <label className="auth-label">
          Buscar
          <input value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>

      {loading ? <p className="auth-muted">Cargando…</p> : null}

      <table className="inventory-table">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Tipo</th>
            <th>Artículo</th>
            <th>Origen → Destino</th>
            <th>Notas</th>
          </tr>
        </thead>
        <tbody>
          {display.map((m) => (
            <tr key={m.id}>
              <td>{m.createdAt.toLocaleString('es-AR')}</td>
              <td>{MOVEMENT_TYPE_LABELS[m.type]}</td>
              <td>
                <Link to={`${modulePaths.inventory}/${m.inventoryItemId}`}>
                  {items.find((i) => i.id === m.inventoryItemId)?.name ??
                    m.inventoryItemId.slice(0, 8)}
                </Link>
              </td>
              <td>
                {(m.fromLocationId ?? '—') + ' → ' + (m.toLocationId ?? '—')}
              </td>
              <td>{m.notes || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && display.length === 0 ? (
        <p className="auth-muted">Sin movimientos.</p>
      ) : null}
    </section>
  );
}
